import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { adminApi } from '@/lib/api';
import { LayoutDashboard, Users, Building2, Wallet, UserCog, ScrollText, ArrowLeft, Download, Mail, Phone, Cake, IdCard, Briefcase } from 'lucide-react';
import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-1 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-sm font-semibold text-[#0D1B2A] break-words">{value || '—'}</div>
      </div>
    </div>
  );
}

function PhotoCard({ title, src, filename }) {
  return (
    <div className="lb-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{title}</div>
        {src && (
          <a href={src} download={filename} className="text-xs text-[#1677FF] flex items-center gap-1 hover:underline">
            <Download className="w-3 h-3" /> Télécharger
          </a>
        )}
      </div>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={title} className="w-full h-52 object-cover rounded-md border border-slate-200 hover:opacity-90" />
        </a>
      ) : (
        <div className="w-full h-52 rounded-md border border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm">Non fournie</div>
      )}
    </div>
  );
}

export default function AdminAgentDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await adminApi.getAgent(id); setA(data); }
      finally { setLoading(false); }
    })();
  }, [id]);

  return (
    <AppLayout links={links} title="Fiche agent">
      <div className="mb-4"><Link to="/admin/agents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0D1B2A]"><ArrowLeft className="w-4 h-4" /> Retour aux agents</Link></div>
      {loading ? <Loader /> : !a ? (
        <div className="lb-card p-8 text-center text-slate-500">Agent introuvable.</div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="lb-card p-6">
              <div className="flex items-center gap-4">
                {a.photo_agent ? (
                  <img src={a.photo_agent} alt={a.full_name} className="w-20 h-20 rounded-full object-cover border-2 border-white ring-2 ring-slate-200" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-400">{(a.prenom||'?')[0]}{(a.nom||'')[0]}</div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-[#0D1B2A]">{a.prenom} {a.nom}</h1>
                  <div className="text-sm text-slate-500">{a.poste || 'Agent'}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                      {a.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    <span className="text-xs text-slate-500">{a.portfolio_count} bailleur(s) sous gestion</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Row icon={Briefcase} label="Poste" value={a.poste} />
                <Row icon={Mail} label="Email" value={a.email} />
                <Row icon={Phone} label="Téléphone" value={a.telephone} />
                <Row icon={UserCog} label="Sexe" value={a.sexe === 'M' ? 'Masculin' : a.sexe === 'F' ? 'Féminin' : '—'} />
                <Row icon={Cake} label="Date de naissance" value={a.date_naissance ? new Date(a.date_naissance).toLocaleDateString('fr-FR') : '—'} />
                <Row icon={IdCard} label={`Pièce (${a.type_piece || '?'})`} value={a.numero_piece} />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <PhotoCard title="Photo agent" src={a.photo_agent} filename={`agent-${a.id}-portrait.jpg`} />
              <PhotoCard title="Pièce — Recto" src={a.photo_piece_recto} filename={`agent-${a.id}-recto.jpg`} />
              <PhotoCard title="Pièce — Verso" src={a.photo_piece_verso} filename={`agent-${a.id}-verso.jpg`} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="lb-card p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Métadonnées</div>
              <div className="mt-3 space-y-3 text-sm">
                <div><div className="text-xs text-slate-500">Créé le</div><div className="font-semibold text-[#0D1B2A]">{a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '—'}</div></div>
                <div><div className="text-xs text-slate-500">Identifiant</div><div className="font-mono text-[11px] text-slate-600 break-all">{a.id}</div></div>
                <div><div className="text-xs text-slate-500">Doit changer le mdp</div><div className="font-semibold text-[#0D1B2A]">{a.must_change_password ? 'Oui' : 'Non'}</div></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
